import React, { useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

const HakkimizdaPage: React.FC = () => {
  useEffect(() => {
    // SEO Meta Tags
    document.title = 'Hakkımızda - Selçuklu Konya İş Sağlığı Güvenliği Özgür OSGB';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Konya Selçuklu Sancak Mahallesi\'nde faaliyet gösteren Özgür OSGB hakkında. 5+ yıl deneyim, 50+ mutlu müşteri. İş Sağlığı ve Güvenliği alanında uzman kadro ile güvenilir hizmet.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Özgür OSGB, Konya İSG, Selçuklu OSGB, İş Güvenliği Firması, İSG Uzmanı');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://ozgurisg.net'}/hakkimizda`);
    }

    // Schema.org JSON-LD
    const existingScript = document.querySelector('script[type="application/ld+json"][data-page="about"]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-page', 'about');
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "Hakkımızda - Özgür OSGB",
      "description": "Konya Selçuklu'da İş Sağlığı ve Güvenliği alanında uzman kadromuzla hizmet veriyoruz",
      "mainEntity": {
        "@type": "Organization",
        "name": "Özgür OSGB",
        "description": "İş Sağlığı ve Güvenliği alanında kapsamlı hizmetler sunan uzman kuruluş",
        "foundingDate": "2019",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Sancak Mahallesi",
          "addressLocality": "Selçuklu",
          "addressRegion": "Konya",
          "addressCountry": "TR"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+90 332 302 82 32",
          "email": "bilgi@ozgurisg.net",
          "contactType": "customer service"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('script[type="application/ld+json"][data-page="about"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-teal-600 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Hakkımızda</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Konya Selçuklu'da İş Sağlığı ve Güvenliği alanında uzman kadromuzla hizmet veriyoruz.
          </p>
        </div>
      </section>

      {/* About Content */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Özgür OSGB</h2>
              <p className="text-lg text-gray-600 mb-6">
                Konya Selçuklu Sancak Mahallesi'nde faaliyet gösteren Özgür OSGB, İş Sağlığı ve Güvenliği alanında 
                kapsamlı hizmetler sunan uzman bir kuruluştur. İşyerlerinde güvenli ve sağlıklı çalışma ortamları 
                oluşturmak için gerekli tüm hizmetleri profesyonel kadromuzla sunmaktayız.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Deneyimli uzman kadromuz, modern yaklaşımlar ve güncel mevzuat bilgisiyle işyerlerinizde 
                İSG standartlarının en üst seviyede uygulanmasını sağlamaktadır.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">50+</div>
                  <div className="text-gray-600">Mutlu Müşteri</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">5+</div>
                  <div className="text-gray-600">Yıl Deneyim</div>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://public.readdy.ai/ai/img_res/e357a28326b5eb258938ee621b9e5c19.jpg"
                alt="Özgür OSGB Ekibi"
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-gray-50 py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-target-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Misyonumuz</h3>
              <p className="text-gray-600">
                İşyerlerinde çalışan sağlığı ve güvenliğini en üst seviyede tutarak, iş kazalarını ve meslek 
                hastalıklarını önlemek. Güvenli çalışma kültürünün yaygınlaşmasına katkıda bulunmak ve 
                işverenlerin yasal yükümlülüklerini yerine getirmelerine destek olmak.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-eye-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Vizyonumuz</h3>
              <p className="text-gray-600">
                Konya ve çevre illerde İş Sağlığı ve Güvenliği alanında öncü ve güvenilir bir kuruluş olmak. 
                Sürekli gelişen teknoloji ve mevzuat değişikliklerini takip ederek müşterilerimize en kaliteli 
                hizmeti sunmak ve sektörde standart belirleyici olmak.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Değerlerimiz</h2>
            <p className="text-xl text-gray-600">
              Çalışma prensiplerimiz ve değerlerimiz
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-4xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Güvenilirlik</h3>
              <p className="text-gray-600">
                Müşterilerimizin güvenini kazanmak ve sürdürmek için her zaman dürüst ve şeffaf hizmet veriyoruz.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-award-line text-4xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Kalite</h3>
              <p className="text-gray-600">
                En yüksek kalite standartlarında hizmet sunarak müşteri memnuniyetini ön planda tutuyoruz.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-lightbulb-line text-4xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Yenilikçilik</h3>
              <p className="text-gray-600">
                Sektördeki gelişmeleri takip ederek yenilikçi çözümler sunmaya devam ediyoruz.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="bg-gray-50 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Uzman Kadromuz</h2>
            <p className="text-xl text-gray-600">
              Alanında uzman ve deneyimli ekibimiz
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20Turkish%20workplace%20safety%20expert%20in%20business%20suit%2C%20confident%20pose%2C%20modern%20office%20background%2C%20professional%20headshot%20style%2C%20clean%20corporate%20environment%2C%20safety%20equipment%20visible%20in%20background&width=300&height=300&seq=team1&orientation=squarish"
                alt="İş Güvenliği Uzmanı"
                className="w-32 h-32 rounded-full mx-auto mb-6 object-cover"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-2">İş Güvenliği Uzmanları</h3>
              <p className="text-green-600 font-medium mb-4">A Sınıfı Uzmanlar</p>
              <p className="text-gray-600">
                Deneyimli iş güvenliği uzmanlarımız ile işyerinizdeki riskleri minimize ediyoruz.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20Turkish%20workplace%20doctor%20in%20white%20coat%20with%20stethoscope%2C%20medical%20professional%20in%20modern%20clinic%20setting%2C%20confident%20healthcare%20provider%2C%20clean%20medical%20environment%2C%20professional%20medical%20portrait&width=300&height=300&seq=team2&orientation=squarish"
                alt="İşyeri Hekimi"
                className="w-32 h-32 rounded-full mx-auto mb-6 object-cover"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-2">İşyeri Hekimleri</h3>
              <p className="text-blue-600 font-medium mb-4">Uzman Doktorlar</p>
              <p className="text-gray-600">
                Çalışan sağlığını korumak için uzman hekimlerimiz düzenli kontroller yapmaktadır.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20Turkish%20safety%20consultant%20with%20safety%20helmet%20and%20clipboard%2C%20workplace%20inspection%20scene%2C%20industrial%20safety%20expert%2C%20modern%20workplace%20environment%2C%20professional%20safety%20assessment&width=300&height=300&seq=team3&orientation=squarish"
                alt="Güvenlik Danışmanı"
                className="w-32 h-32 rounded-full mx-auto mb-6 object-cover"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Güvenlik Danışmanları</h3>
              <p className="text-purple-600 font-medium mb-4">Sertifikalı Uzmanlar</p>
              <p className="text-gray-600">
                İSG konularında danışmanlık hizmeti veren uzman kadromuz her zaman yanınızda.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Bizimle Çalışmaya Başlayın
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            İşyerinizdeki güvenlik standartlarını yükseltmek için uzman kadromuzla iletişime geçin.
          </p>
          <a 
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.open('https://wa.me/903323028232?text=Merhaba, İSG hizmetleriniz hakkında bilgi almak istiyorum.', '_blank');
            }}
            className="inline-flex items-center justify-center font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap bg-green-600 text-white hover:bg-green-700 px-8 py-4 text-lg"
          >
            İletişime Geçin
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HakkimizdaPage;